import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingBag, LogOut, Menu, X } from 'lucide-react';
import { useAdmin } from '../../contexts/AdminContext';

const AdminLayout = ({ children }) => {
  const { logout } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const menuItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Products', path: '/admin/products', icon: Package },
    { name: 'Orders', path: '/admin/orders', icon: ShoppingBag }
  ];

  const isActive = (path) => location.pathname === path;

  const NavContent = () => (
    <>
      <div className="p-5 lg:p-6">
        <div className="flex items-center space-x-3">
          <img src="https://customer-assets.emergentagent.com/job_behad-living/artifacts/rxtdz05y_Behat_Atelier.png" alt="Behat House Logo" className="h-9 lg:h-10 w-auto object-contain brightness-150" />
          <div>
            <h2 className="text-sm lg:text-base font-serif leading-tight">Behat House</h2>
            <p className="text-[8px] lg:text-[9px] text-[#C4A265] tracking-[0.3em] uppercase font-semibold">Admin Panel</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 mt-2">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.path} to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center space-x-3 px-5 lg:px-6 py-3 text-sm transition-colors ${
                isActive(item.path)
                  ? 'bg-[#C4A265]/20 border-l-3 border-[#C4A265] text-[#C4A265]'
                  : 'text-white/70 hover:bg-white/5 hover:text-white'
              }`}>
              <Icon className="w-5 h-5" />
              <span>{item.name}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-5 lg:p-6 border-t border-white/10">
        <button onClick={handleLogout} className="flex items-center space-x-3 text-white/60 hover:text-[#C4A265] transition-colors text-sm">
          <LogOut className="w-5 h-5" /><span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FEFCF9]">
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-40 bg-[#3E2723] text-white flex items-center justify-between px-4 py-3">
        <div className="flex items-center space-x-2">
          <img src="https://customer-assets.emergentagent.com/job_behad-living/artifacts/rxtdz05y_Behat_Atelier.png" alt="Logo" className="h-8 w-auto brightness-150" />
          <div>
            <p className="text-sm font-serif leading-tight">Behat House</p>
            <p className="text-[7px] text-[#C4A265] tracking-[0.2em] uppercase font-semibold">Admin</p>
          </div>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="admin-menu-toggle">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#3E2723] text-white flex flex-col">
            {/* Close button inside sidebar */}
            <div className="flex justify-end p-3">
              <button onClick={() => setSidebarOpen(false)} className="p-1 hover:bg-white/10 rounded">
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
            <NavContent />
          </aside>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 bg-[#3E2723] text-white fixed h-full flex-col">
        <NavContent />
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
